import {CacheDataService} from '../../../api/services/cache-data.service';
import {FlatTreeControl} from '@angular/cdk/tree';
import {TreeViewSelectionNode, TreeViewSelectionState} from '../helpers/tree-view-selection-node';
import {TreeViewDataSource} from '../helpers/tree-vew-data-source';

export abstract class FilterEditorComponent<TSourceType, TSourceKeyType, TNodeType extends TreeViewSelectionNode<TSourceType, TSourceKeyType>, TDataSourceType extends TreeViewDataSource<TSourceType, TSourceKeyType, TNodeType>> {
  protected constructor(private cacheDataService : CacheDataService) {
    this.treeControl = new FlatTreeControl<TNodeType>(this.getLevel, this.isExpandable);
    this.dataSource = this.getNewDataSource(this.treeControl, this.cacheDataService);
    this.dataSource.loadRootItems();

    this.dataSource.dataChange.subscribe(x => {
      this.setSelections();
    })
  }

  protected _value: TSourceKeyType[] = [];
  private _onChangedCallback: (value: TSourceKeyType[]) => void = () => {};
  private _onTouchedCallback: () => void = () => {};

  treeControl: FlatTreeControl<TNodeType>;

  dataSource: TDataSourceType;

  getLevel = (node: TNodeType) => node.level;

  isExpandable = (node: TNodeType) => node.expandable;

  hasChild = (_: number, _nodeData: TNodeType) => _nodeData.expandable;

  protected abstract getNewDataSource(treeControl: FlatTreeControl<TNodeType>, cacheDataService: CacheDataService): TDataSourceType;

  private getSelections(node: TNodeType): TSourceKeyType[] {
    const result: TSourceKeyType[] = [];
    if (node.selectionState(this._value) == TreeViewSelectionState.Selected || node.selectionState(this._value) == TreeViewSelectionState.AllChildrenSelected) {
      result.push(node.sourceKey);
    }
    if (node.children) {
      node.children.forEach(x => result.push(...this.getSelections(x as TNodeType)));
    }
    return result;
  }

  private setSelection(node: TNodeType) {
    const target = this._value?.find(x => node.matchesKey(x));

    if (target) {
      node.setSelection(true);
    } else {
      node.setSelection(false);
    }

    node.children.forEach(x => this.setSelection(x as TNodeType));
  }

  private setSelections() {
    this.dataSource.data.forEach(x => this.setSelection(x));
  }

  toggleSelection(node: TNodeType) {
    if (node.selectionState(this._value) > TreeViewSelectionState.Selected) {
      node.setSelection(false);
    } else {
      node.toggleSelection();
    }

    const selections: TSourceKeyType[] = [];

    // Collect selections from visible nodes
    this.dataSource.data.forEach(node => selections.push(...this.getSelections(node)));

    // PRESERVE selections from _value that aren't in the current visible tree
    // This keeps pre-loaded selections for nodes that haven't been expanded yet
    if (this._value) {
      this._value.forEach(existingKey => {
        // Check if this key is represented in the current visible tree
        const isInVisibleTree = this.isKeyInVisibleTree(existingKey, this.dataSource.data);
        if (!isInVisibleTree) {
          selections.push(existingKey);
        }
      });
    }

    this.value = selections;
  }

  // Helper method to check if a key is represented in the visible tree
  private isKeyInVisibleTree(key: TSourceKeyType, nodes: TNodeType[]): boolean {
    for (const node of nodes) {
      if (node.matchesKey(key)) {
        return true;
      }
      if (node.children && node.children.length > 0) {
        if (this.isKeyInVisibleTree(key, node.children as TNodeType[])) {
          return true;
        }
      }
    }
    return false;
  }
  get value(): any {
    return this._value;
  }

  set value(v: any) {
    if (v !== this._value) {
      this._value = v;
      this.setSelections();
      this.dataSource.refresh();
      this._onChangedCallback(v);
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this._onChangedCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Implement if needed
  }

  protected readonly TreeViewSelectionState = TreeViewSelectionState;
}
